import { createFileRoute, Link } from "@tanstack/react-router";
import { useReveal } from "@/hooks/use-reveal";
import { SeoAccordion } from "@/components/seo-accordion";
import { DJ_VAL_SECTIONS } from "@/data/seo-blocks";
import { trackEvent } from "@/lib/analytics";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
import { LOCAL_BUSINESS_SCHEMA } from "@/lib/schema-org";
const HERO = "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1920&q=75&auto=format&fit=crop";
const IMG = {
  booth: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?auto=format&fit=crop&w=1200&q=72",
  dance: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=1200&q=72",
  lights: "https://images.unsplash.com/photo-1493676304819-0d7a8d026dcf?auto=format&fit=crop&w=1200&q=72",
  speakers: "https://images.unsplash.com/photo-1518972559570-7cc1309f3229?auto=format&fit=crop&w=1200&q=72",
  reception: "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?auto=format&fit=crop&w=1200&q=72",
  cocktail: "https://images.unsplash.com/photo-1470337458703-46ad1756a187?auto=format&fit=crop&w=1200&q=72",
};

const TITLE = "DJ Vaucluse – Mariage, anniversaire, entreprise | DJ Val";
const DESC =
  "DJ dans le Vaucluse depuis 20 ans : soirées privées, anniversaires, entreprise, mariages. Avignon, Cavaillon, Carpentras, Orange, Apt, Luberon. Devis en 24 h.";
const CANON = "https://dj-val.fr/dj-vaucluse";


export const Route = createFileRoute("/dj-vaucluse")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: metaDesc(DESC) },
      { name: "keywords", content: "DJ Vaucluse, DJ 84, DJ Avignon, DJ Cavaillon, DJ dans le Vaucluse, DJ professionnel Vaucluse" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: metaDesc(DESC) },
      { property: "og:url", content: CANON },
      { property: "og:image", content: HERO },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: CANON },
      { rel: "preload", as: "image", href: HERO, fetchpriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({ ...LOCAL_BUSINESS_SCHEMA, "@context": "https://schema.org", name: "DJ Val – DJ Vaucluse", image: HERO, areaServed: [
            { "@type": "AdministrativeArea", name: "Vaucluse" },
            { "@type": "City", name: "Avignon" },
            { "@type": "City", name: "Cavaillon" },
            { "@type": "City", name: "Carpentras" },
            { "@type": "City", name: "Orange" },
            { "@type": "City", name: "Apt" },
            { "@type": "City", name: "Pertuis" },
          ] }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Quel est le tarif d'un DJ dans le Vaucluse ?", acceptedAnswer: { "@type": "Answer", text: "Entre 690 € (anniversaire 5 h) et 2 190 € (mariage Prestige 10 h). Mariages à partir de 1 490 €. Formules transparentes : sono Line Array, éclairage LED, micros HF, installation et démontage compris. Aucun supplément matériel." } },
            { "@type": "Question", name: "Quelle zone couvrez-vous dans le Vaucluse ?", acceptedAnswer: { "@type": "Answer", text: "Tout le département 84 sans frais : Avignon, Cavaillon, Carpentras, Orange, Apt, Pertuis, Vaison-la-Romaine, L'Isle-sur-la-Sorgue, Sault, Bollène, Valréas, Gordes, Roussillon, Bonnieux, Ménerbes." } },
            { "@type": "Question", name: "Pourquoi DJ Val plutôt qu'un autre DJ Vaucluse ?", acceptedAnswer: { "@type": "Answer", text: "20 ans d'expérience locale, 500+ événements, note Google 5/5. Connaissance fine des domaines vauclusiens, limiteur calibré inclus, 1 seul événement par week-end — votre soirée est ma priorité absolue." } },
            { "@type": "Question", name: "Animez-vous d'autres événements que les mariages ?", acceptedAnswer: { "@type": "Answer", text: "Oui : soirées d'entreprise (CE, kick-off, gala), anniversaires (30/40/50/60 ans), baptêmes, communions, soirées thématiques (fluo, karaoké, gender reveal), Saint-Sylvestre." } },
            { "@type": "Question", name: "Quel matériel apportez-vous ?", acceptedAnswer: { "@type": "Answer", text: "Pioneer CDJ-3000 + DJM-A9, Line Array RCF, 4 à 8 jeux d'éclairage LED, lyres, lasers, 2 micros HF Shure, machine à fumée, photobooth en option. Backup complet sur place." } },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://dj-val.fr/" },
            { "@type": "ListItem", position: 2, name: "DJ Vaucluse", item: CANON },
          ],
        }),
      },
    ],
  }),
  component: DjVauclusePage,
});

function DjVauclusePage() {
  useReveal();

  return (
    <div className="bg-background text-muted-foreground">
      {/* HERO */}
      <section className="on-dark relative min-h-[88vh] flex items-center px-6 overflow-hidden pt-24">
        <div className="absolute inset-0 z-0">
          <img src={HERO} alt="DJ Val en pleine animation d'une soirée dans le Vaucluse" className="h-full w-full object-cover opacity-45 kenburns-img" fetchPriority="high" width={1920} height={1080} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, var(--ink) 0%, transparent 38%, var(--ink) 100%)" }} />
        </div>
        <div className="relative z-10 max-w-3xl reveal space-y-6">
          <p className="eyebrow">DJ Vaucluse · Depuis 2010</p>
          <h1 className="font-serif-display text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            <span className="italic font-light">DJ dans le Vaucluse :</span>
            <span className="block font-light gold-text-shine mt-2">mariage, soirées et événements</span>
          </h1>
          <span className="gold-rule !w-12" />
          <p className="text-base text-foreground/85 font-light leading-relaxed max-w-xl">
            500+ événements animés dans le 84 — soirées privées, anniversaires, soirées d'entreprise, mariages. Avignon, Cavaillon, Apt, Carpentras, Orange, Pertuis, tout le Luberon. Sono Line Array et éclairage LED inclus.
          </p>

          <div className="flex flex-wrap gap-4">
            <Link to="/contactez-nous" className="bg-[var(--gold)] text-[var(--ink)] px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all">
              Devis gratuit 24 h
            </Link>
            <a href="tel:0767908667" onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "dj_vaucluse" })} className="border border-[var(--gold-line)] text-foreground px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
              07 67 90 86 67
            </a>
          </div>
          <div className="flex flex-wrap gap-6 pt-6 text-xs uppercase tracking-[0.2em] text-foreground/85">
            <span>★ 5 / 5 Google</span>
            <span>· 500+ événements</span>
            <span>· 20 ans d'expérience</span>
          </div>
        </div>
      </section>
      {/* Encart de désambiguïsation — renvoie l'intention « mariage » vers la page dédiée */}
      <section className="border-y border-[var(--gold-line)] bg-card px-6 py-5">
        <div className="max-w-4xl mx-auto text-center text-sm font-light space-y-2">
          <p className="text-muted-foreground">
            Cette page couvre l'ensemble de mes prestations dans le département. Pour un mariage, tout le détail —
            déroulé, formules dès 1 490 €, lieux de réception — se trouve sur la page{" "}
            <Link
              to="/dj-mariage/$ville"
              params={{ ville: "vaucluse" }}
              className="text-[var(--gold)] underline underline-offset-4 hover:brightness-110 transition-all"
            >
              DJ mariage dans le Vaucluse
            </Link>
            .
          </p>
          <p className="text-muted-foreground">
            Vous cherchez un prestataire sur la préfecture ?{" "}
            <Link to="/dj-avignon" className="text-[var(--gold)] underline underline-offset-4 hover:brightness-110 transition-all">
              Voir la page DJ à Avignon
            </Link>
            .
          </p>
        </div>
      </section>

      <ReviewsProof />

      {/* CHIFFRES */}
      <section className="border-y border-[var(--gold-line)] bg-card">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4">
          {[
            { n: "500+", l: "Événements signés" },
            { n: "300+", l: "Mariages Vaucluse" },
            { n: "20 ans", l: "Sur le terrain 84" },
            { n: "5/5", l: "Note Google" },
          ].map((s) => (
            <div key={s.n} className="p-10 text-center border-r last:border-r-0 border-[var(--gold-line)]">
              <p className="font-serif-display text-4xl text-[var(--gold)] italic">{s.n}</p>
              <p className="eyebrow text-foreground/85 mt-3">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRESTATIONS */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Toutes vos soirées</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            Un DJ Vaucluse pour <span className="italic">chaque occasion</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="grid gap-px bg-[var(--gold-line)] md:grid-cols-2 lg:grid-cols-3 border border-[var(--gold-line)]">
          {[
            { t: "DJ mariage Vaucluse", d: "La page dédiée au mariage : déroulé de la soirée, formules dès 1 490 €, lieux de réception du département.", to: "/dj-mariage/vaucluse" },
            { t: "DJ Avignon", d: "Toutes mes prestations sur Avignon et sa périphérie : mariage, soirée privée, entreprise, karaoké, photobooth.", to: "/dj-avignon" },
            { t: "DJ Entreprise Vaucluse", d: "Galas, séminaires, kick-off, soirées CE. Facturation pro, SACEM réglée, assurance Hiscox.", to: "/dj-entreprise/avignon" },
            { t: "DJ Anniversaire Vaucluse", d: "30, 40, 50, 60 ans, surprise party. Forfait clé-en-main dès 690 €.", to: "/dj-anniversaire/avignon" },

            { t: "Location Karaoké", d: "Pack karaoké complet dès 200 € : système son, vidéoprojecteur, écran de projection et 2 micros.", to: "/location-materiel/karaoke" },
            { t: "Location de sono", d: "Sans DJ : pack sono + lumière en location, livraison et installation au choix.", to: "/location-sono-mariage" },
            { t: "Gender Reveal", d: "Pack canon CO2 + animation surprise pour révéler le sexe de bébé.", to: "/location-gender-reveal" },
          ].map((p, i) => (
            <Link key={p.t} to={p.to} className="bg-background p-10 sr block hover:bg-card transition-colors" data-reveal style={{ transitionDelay: `${i * 60}ms` }}>
              <p className="font-serif-display italic text-[var(--gold)] text-2xl">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="font-serif-display text-2xl text-foreground mt-4">{p.t}</h3>
              <span className="gold-rule !w-8 my-4" />
              <p className="text-sm text-muted-foreground leading-relaxed font-light">{p.d}</p>
              <span className="mt-5 inline-block text-[11px] uppercase tracking-[0.2em] text-foreground">Voir →</span>
            </Link>
          ))}
        </div>
      </section>

      {/* AMBIANCE */}
      <section className="py-20 px-6 border-y border-[var(--gold-line)] bg-card">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sr" data-reveal>
            <p className="eyebrow text-[var(--gold)]">L'ambiance signature</p>
            <h2 className="font-serif-display text-4xl text-foreground mt-4">
              Le <span className="italic">style DJ Val</span> dans le Vaucluse
            </h2>
            <span className="gold-rule mx-auto !w-12 mt-6" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { src: IMG.dance, alt: "Piste de danse pleine lors d'une soirée DJ dans le Vaucluse" },
              { src: IMG.lights, alt: "Mise en lumière LED pour mariage dans le Vaucluse" },
              { src: IMG.booth, alt: "Régie DJ Pioneer CDJ-3000 lors d'un événement vauclusien" },
              { src: IMG.reception, alt: "Réception de mariage en Provence animée par DJ Val" },
              { src: IMG.speakers, alt: "Sono Line Array RCF installée pour une soirée Vaucluse" },
              { src: IMG.cocktail, alt: "Vin d'honneur sonorisé en extérieur dans le Vaucluse" },
            ].map((img, i) => (
              <div key={i} className="group overflow-hidden border border-[var(--gold-line)] sr aspect-[4/3]" data-reveal style={{ transitionDelay: `${(i % 3) * 80}ms` }}>
                <img src={img.src} alt={img.alt} loading="lazy" decoding="async" width={800} height={600} className="h-full w-full object-cover transition-transform duration-[1500ms] group-hover:scale-110" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VILLES */}
      <section className="py-24 px-6 max-w-6xl mx-auto">
        <div className="text-center mb-12 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Toutes les communes</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">
            DJ dans <span className="italic">tout le Vaucluse</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
          <p className="text-sm text-muted-foreground font-light mt-6 max-w-2xl mx-auto">
            Aucun frais de déplacement dans le département 84. Pages dédiées avec retours d'expérience par ville.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {[
            ["avignon", "Avignon"], ["cavaillon", "Cavaillon"], ["apt", "Apt"],
            ["carpentras", "Carpentras"], ["orange", "Orange"], ["pertuis", "Pertuis"],
            ["luberon", "Luberon"], ["vaucluse", "Vaucluse (84)"],
          ].map(([slug, name]) => (
            <Link key={slug} to="/dj-mariage/$ville" params={{ ville: slug }} className="border border-[var(--gold-line)] bg-card px-5 py-4 text-center text-foreground/85 text-sm uppercase tracking-[0.15em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
              {name}
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 max-w-4xl mx-auto border-t border-[var(--gold-line)]">
        <div className="text-center mb-12 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Questions fréquentes</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">DJ Vaucluse · ce qu'on me demande</h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="space-y-4">
          {[
            { q: "Quel est le tarif d'un DJ dans le Vaucluse ?", a: "Entre 690 € (anniversaire 5 h) et 2 190 € (mariage Prestige 10 h). Mariages à partir de 1 490 €. Formules transparentes : sono Line Array, éclairage LED, micros HF, installation et démontage compris. Aucun supplément matériel." },
            { q: "Quelle zone couvrez-vous dans le Vaucluse ?", a: "Tout le département 84 sans frais : Avignon, Cavaillon, Carpentras, Orange, Apt, Pertuis, Vaison-la-Romaine, L'Isle-sur-la-Sorgue, Sault, Bollène, Valréas, Gordes, Roussillon, Bonnieux, Ménerbes." },
            { q: "Pourquoi DJ Val plutôt qu'un autre DJ Vaucluse ?", a: "20 ans d'expérience locale, 500+ événements, note Google 5/5. Connaissance fine des domaines vauclusiens, limiteur calibré inclus, 1 seul événement par week-end." },
            { q: "Animez-vous d'autres événements que les mariages ?", a: "Oui : soirées d'entreprise (CE, kick-off, gala), anniversaires (30/40/50/60 ans), baptêmes, communions, soirées thématiques (fluo, karaoké, gender reveal), Saint-Sylvestre." },
            { q: "Quel matériel apportez-vous ?", a: "Pioneer CDJ-3000 + DJM-A9, Line Array RCF, 4 à 8 jeux d'éclairage LED, lyres, lasers, 2 micros HF Shure, machine à fumée, photobooth en option. Backup complet sur place." },
            { q: "Combien de temps à l'avance faut-il réserver ?", a: "10 à 14 mois pour un mariage en haute saison (mai–septembre). 4 à 8 semaines pour un anniversaire ou une soirée d'entreprise hors saison." },
          ].map((f) => (
            <details key={f.q} className="group border border-[var(--gold-line)] bg-card p-6 sr" data-reveal>
              <summary className="cursor-pointer font-serif-display text-xl text-foreground flex justify-between items-center gap-4">
                {f.q}
                <span className="text-[var(--gold)] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed font-light">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center border-t border-[var(--gold-line)]">
        <div className="max-w-2xl mx-auto sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Prêt à réserver ?</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">
            Votre soirée dans le Vaucluse <span className="italic text-[var(--gold)]">mérite</span> un DJ pro
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <p className="text-sm text-muted-foreground font-light max-w-xl mx-auto mb-8">
            Réponse en moins de 24 h. Devis détaillé, transparent, sans engagement.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contactez-nous" className="inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all">
              Demander un devis
            </Link>
            <Link to="/calculateur" className="inline-block border border-[var(--gold-line)] text-foreground px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
              Calculer mon tarif
            </Link>
          </div>
        </div>
      </section>

      <SeoAccordion
        eyebrow="DJ Vaucluse"
        title={<>Vos questions sur un <span className="italic">DJ dans le Vaucluse</span></>}
        intro="Tarifs, délais, lieux, matériel : tout ce qu'on nous demande avant de réserver un DJ pour un événement dans le département."
        sections={DJ_VAL_SECTIONS}
      />
    </div>
  );
}
